import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IContactActual } from 'legacy/lut/classes/models/IContactActual'
import { IFormPartners } from 'legacy/lut/classes/models/IFormPartners'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { isAppLut, isAppOwnerProduction } from 'legacy/lut/functions'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ContactEditor } from 'clickNgo/containers/contactEditor'
import { getContactStateKey, IContact } from 'clickNgo/entities/contact'
import { useGetContactsWithFlagsByPartnerQuery } from 'clickNgo/entities/contact/api'
import { emptyContactFlags } from 'clickNgo/entities/contactFlag'
import PartnerContacts from 'clickNgo/widgets/partnerContacts'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
  getFullPhone,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'
import { OnboardingInput, OnboardingPhoneEditor } from 'onboarding/components/ui/theme'
import { ONBOARDING_CONTACTS_CONFIG } from 'onboarding/configs/onboardingContactsConfig'

import { useContactData } from '../../useContactData'
import { ICollectorData } from '../ICollectorData'

import { EndUser, IEndUser } from './contractValidate/EndUser'
import { FormContext } from './FormContext'
import { IContractValidateProps, IInputField } from './models'

import styles from './ContractValidate.module.scss'

const ALLOWED_MODIFY_FIELDS = [
  'NotCorporateEmailReason',
  'NotCorporateEmailReasonComment',
  'JobTitle',
]

const checkIsFieldAllowedToBeModified = (field: string) => ALLOWED_MODIFY_FIELDS.includes(field)

export const ContractValidate = ({
  contactFields,
  partnerFields,
  PartnerID,
  RequestId,
  showEndUserInfo,
  appSource,
  disabled,
}: IContractValidateProps) => {
  const { requestId, parentRequest } = useContext(FormContext)

  const sourcePartner = useMemo(() => {
    return CrudStore.get(CrudStoreItem.PartnerAdditionalInfo, 'Contract Validate')
  }, [])

  const sourceContact = useMemo(() => {
    return CrudStore.get(CrudStoreItem.ContactAdditionalInfo, 'Contract Validate Additional')
  }, [])

  const collectedDataSource = useMemo(() => {
    return CrudStore.get(CrudStoreItemOnboarding.CollectedData, 'ContractValidateForm')
  }, [])

  const endUserSource = useMemo(() => {
    return CrudStore.get(CrudStoreItemOnboarding.ContractValidateAddition)
  }, [])

  const [endUserChange, setEndUserChange] = useState<boolean>(false)
  const [endUser, setEndUser] = useState<IEndUser>({
    RequestId: RequestId || -1,
  } as IEndUser)

  useEffect(() => {
    if (showEndUserInfo) {
      endUserSource
        .getById({ id: endUser.RequestId, keyField: 'RequestId' })
        .then((el: IEndUser) => {
          el && setEndUser(el)
        })
    }
  }, [endUser.RequestId])

  const [partner, setPartner] = useState<IFormPartners | null>(null)
  const [contactData, setContactData] = useState<IContactActual>({} as IContactActual)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [collectedItem, setCollectedItem] = useState({} as ICollectorData)
  const [isContactCleared, setIsContactCleared] = useState(false)

  const setAllowedContactDataOnly = useCallback((contact: IFormProfile) => {
    const result: IContactActual = {} as IContactActual
    ALLOWED_MODIFY_FIELDS.forEach((item: string) => {
      result[item] = contact[item]
    })
    setContactData(result)
  }, [])

  const { contact, conWip } = useContactData({
    mainFilter: {
      field: RequestId ? 'ContactID' : 'PartnerID',
      value: RequestId ? collectedItem.Value : PartnerID,
    },
    additionalFilter: RequestId ? undefined : { field: 'PR_HP_ROLE', value: 'General Manager' },
    onLoadContact: setAllowedContactDataOnly,
  })
  const contactDataModel = new DataModel(contact)
  const [opened, setOpened] = useState(false)
  const [wip, setWip] = useState(false)

  const { data: editorContactState, refetch: refetchEditorContacts } =
    useGetContactsWithFlagsByPartnerQuery(
      { isOnboarding: true, partnerId: PartnerID },
      { skip: !PartnerID }
    )

  const editableContactId = contactDataModel.get('ContactID') || collectedItem.Value

  const editableContact = useMemo(() => {
    return (
      Object.values<IContact>(editorContactState?.contactsById || {}).find(
        (item) => item.contactId === editableContactId
      ) || null
    )
  }, [editableContactId, editorContactState?.contactsById])

  const editableContactWithOnboardingRequestId = useMemo(() => {
    if (!editableContact) {
      return null
    }

    return {
      ...editableContact,
      onboardingRequestId:
        editableContact.onboardingRequestId ?? (RequestId ? Number(RequestId) : undefined),
    }
  }, [editableContact, RequestId])

  const editableContactFlags = useMemo(() => {
    if (!editableContact) {
      return emptyContactFlags
    }

    return (
      editorContactState?.contactFlagsById[getContactStateKey(editableContact)] || emptyContactFlags
    )
  }, [editableContact, editorContactState?.contactFlagsById])

  const handleContactSelect = useCallback((selectedContact: IContact) => {
    setOpened(false)
    setCollectedItem((prev) => ({ ...prev, Value: selectedContact.contactId }))
  }, [])

  const fetchAssignedContact = useCallback(async () => {
    let searchRequestId = requestId
    if (parentRequest && parentRequest.type === RequestTypes.Onboarding) {
      searchRequestId = parentRequest.id
    }

    if (searchRequestId !== -1) {
      const filters = [
        {
          field: 'RequestId',
          operator: Operator.EQUAL,
          value: searchRequestId,
        },
        {
          field: 'Field',
          operator: Operator.EQUAL,
          value: 'ContactValidFormSelectedContact',
        },
      ]

      collectedDataSource.setFilter({
        logic: Logic.AND,
        filters,
      })
      const fetched = await collectedDataSource.fetch({ force: true })
      return fetched.items[0] || {}
    }
    return {}
  }, [requestId, parentRequest?.id])

  useEffect(() => {
    const fetch = async () => {
      setWip(true)
      try {
        const [collectedItem, partner] = await Promise.all([
          fetchAssignedContact(),
          sourcePartner.getById({ id: PartnerID, keyField: 'PartnerID' }),
        ])
        setPartner(partner)
        setCollectedItem(collectedItem)
      } catch (e) {
        Logger.error(e)
      } finally {
        setWip(false)
      }
    }

    fetch()
  }, [PartnerID, fetchAssignedContact])

  const onSave = async () => {
    setWip(true)
    const savedCollectedItem = {
      ...collectedItem,
      Value: isContactCleared ? '' : collectedItem.Value,
    }
    if (savedCollectedItem.Value && PartnerID) {
      const resContact = await sourceContact.getById({
        id: savedCollectedItem.Value,
        force: true,
        keyField: 'ContactID',
      })

      const resItem = resContact?.EntryId
        ? { ...contactData, EntryId: resContact.EntryId }
        : { ...contactData, PartnerID, ContactID: savedCollectedItem.Value }

      const method = resContact?.EntryId ? 'update' : 'create'
      await sourceContact[method]({ formId: '379', code: 'ContactAdditionalInfo' }, [resItem])
    }
    if (partner) {
      await sourcePartner.update({ formId: '458', code: 'PartnerAdditionalInfo' }, [partner])
    }
    if (requestId !== -1) {
      const formData: ICollectorData = await fetchAssignedContact()
      let searchRequestId = requestId
      if (parentRequest && parentRequest.type === RequestTypes.Onboarding) {
        searchRequestId = parentRequest.id
      }
      const dataToUpdateOrCreate = formData.EntryId
        ? { ...formData, ...savedCollectedItem }
        : savedCollectedItem
      await collectedDataSource[dataToUpdateOrCreate.EntryId ? 'update' : 'create']('677', [
        {
          ...dataToUpdateOrCreate,
          Field: 'ContactValidFormSelectedContact',
          RequestId: searchRequestId,
          Type: 'FromComplexForm',
        },
      ])
    }
    if (RequestId && showEndUserInfo && endUserChange) {
      const endUserData: IEndUser | null = await endUserSource.getById({
        id: endUser.RequestId,
        force: true,
        keyField: 'RequestId',
      })
      const endUserToUpdateOrCreate = endUserData ? { ...endUserData, ...endUser } : endUser
      await endUserSource[endUserData ? 'update' : 'create'](
        { formId: '831', code: 'contract-validate-additionalinfo-831' },
        [endUserToUpdateOrCreate]
      )
      setEndUserChange(false)
    }
    setWip(false)
  }

  const getValue = (field: string) => {
    const value = checkIsFieldAllowedToBeModified(field)
      ? contactData[field]
      : contactDataModel.get(field)

    return value || ''
  }

  const onClear = () => {
    setIsContactCleared(true)
  }

  const openModalDialog = () => {
    setOpened(true)
    setIsContactCleared(false)
  }

  const assignContact = (contactAlreadySelected: boolean) => {
    if (contactAlreadySelected) {
      return (
        <>
          <Button className={styles.btn} onClick={openModalDialog} disabled={disabled}>
            Reselect Contact
          </Button>
          <Button onClick={onClear} disabled={disabled}>
            Clear
          </Button>
        </>
      )
    } else {
      return (
        <div className={styles.root__contact}>
          <p>Contact isn’t assigned to this request</p>
          <Button onClick={openModalDialog} disabled={disabled}>
            Select Contact
          </Button>
        </div>
      )
    }
  }

  return conWip || wip ? (
    <Loader />
  ) : (
    <div className={styles.root}>
      <div className={styles.body}>
        {!isContactCleared && contact && Object.keys(contact).length ? (
          <div className={styles.body__contact}>
            <div className={styles.infoLabel}>Contact Information</div>
            {contactFields.map((item: IInputField, index: number) => {
              const isDisabled = disabled || !checkIsFieldAllowedToBeModified(item.field)
              return (
                <div key={index} className={styles.input}>
                  {item.field === 'WorkPhone' ? (
                    <div className={styles.input__phone}>
                      <CallablePhoneWrapper
                        callMode={CALL_MODE.byIcon}
                        type={MY_CONNECT_TYPE.PartnerContact}
                        phone={getFullPhone(
                          contactDataModel.get('WorkPhone_Prefix'),
                          contactDataModel.get('WorkPhone')
                        )}
                        additionalAttributes={{
                          partnerId: PartnerID,
                          contactProcessedInScout: isAppLut(),
                        }}
                      >
                        <OnboardingPhoneEditor
                          isDisabled={isDisabled}
                          label={item.label || ''}
                          codeValue={contactDataModel.get('WorkPhone_Prefix') || ''}
                          phoneValue={contactDataModel.get('WorkPhone') || ''}
                          onCodeChange={() => {}}
                          onPhoneChange={() => {}}
                        />
                      </CallablePhoneWrapper>
                    </div>
                  ) : (
                    <OnboardingInput
                      label={item.label}
                      disabled={isDisabled}
                      onChange={(value: string) =>
                        setContactData((oldState) => ({
                          ...oldState,
                          [item.field]: value,
                        }))
                      }
                      value={getValue(item.field)}
                    />
                  )}
                </div>
              )
            })}
            <div className={styles.footer}>
              <Button className={styles.btn} onClick={() => setShowModal(true)} disabled={disabled}>
                Edit Manager Information
              </Button>
              {RequestId && assignContact(true)}
            </div>
          </div>
        ) : (
          <div className={styles.body__contact}>
            {RequestId ? (
              assignContact(false)
            ) : (
              <p>
                Can not find contact with Partner ID {PartnerID} and Role 'General Manager' in
                ContactAllData and ContactExt
              </p>
            )}
          </div>
        )}
        <div className={styles.additionalInfo}>
          {partner && Object.keys(partner).length ? (
            <div className={styles.body__partner}>
              <div className={styles.infoLabel}>Partner Info</div>
              {partnerFields.map((item: IInputField, index: number) => (
                <div key={index}>
                  {item.type !== 'boolean' ? (
                    <OnboardingInput
                      classes={{
                        root: styles.input__partner,
                      }}
                      label={item.label}
                      value={partner[item.field]}
                      onChange={(value: string) => {
                        setPartner({ ...partner, [item.field]: value })
                      }}
                      margin={'md'}
                      disabled={disabled}
                    />
                  ) : (
                    <Checkbox
                      margin={'md'}
                      label={item.JSXLabel}
                      checked={!!partner[item.field]}
                      onChange={(value: boolean) =>
                        setPartner({ ...partner, [item.field]: value ? 1 : 0 })
                      }
                      disabled={disabled}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.body__partner}>
              <p>
                Can not find partner with Partner ID {PartnerID} and Role 'General Manager' in
                PartnerAllData and NewPartner
              </p>
            </div>
          )}
          {showEndUserInfo && !isAppOwnerProduction ? (
            <EndUser
              endUser={endUser}
              onChange={(value: string, field: string) => {
                !endUserChange && setEndUserChange(true)
                setEndUser({ ...endUser, [field]: value })
              }}
              disabled={disabled}
            />
          ) : null}
        </div>

        {showModal && editableContactWithOnboardingRequestId && (
          <ContactEditor
            contact={editableContactWithOnboardingRequestId}
            contactFlags={editableContactFlags}
            isVisible={Boolean(editableContact)}
            source={appSource}
            isReadOnly={disabled}
            isNewContact={false}
            isOnboarding
            onClose={() => setShowModal(false)}
            onContactUpdate={() => {
              refetchEditorContacts()
            }}
            onContactFlagsUpdate={() => {
              refetchEditorContacts()
            }}
          />
        )}
        {showModal && !editableContact && (
          <ModalDialog
            show={showModal}
            onClose={() => setShowModal(false)}
            size="md"
            content={() => <Loader />}
          />
        )}
        {opened && PartnerID && (
          <ModalDialog
            show={opened}
            onClose={() => setOpened(false)}
            size="xl"
            content={() => (
              <PartnerContacts
                partnerId={PartnerID}
                source={appSource}
                config={ONBOARDING_CONTACTS_CONFIG}
                onContactSelect={handleContactSelect}
              />
            )}
          />
        )}
      </div>
      <div className={styles.footer}>
        <Button onClick={onSave} disabled={disabled}>
          Save
        </Button>
      </div>
    </div>
  )
}
